import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router';
import { ChangeEvent, SubmitEvent, useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import { Autocomplete, CircularProgress, MenuItem, Typography } from '@mui/material';
import { useQuery } from '@apollo/client/react';
import type { Vendor } from '../../models/Vendor';
import { GET_CONTACTS_QUERY } from '../../queries/ContactQueries';
import { State, states } from '../../models/State';

export interface VendorFormState {
  values: Partial<Omit<Vendor, 'id'>>;
  errors: Partial<Record<keyof VendorFormState['values'], string>>;
}

export interface VendorFormProps {
  formState: VendorFormState;
  onFieldChange: (
    name: keyof VendorFormState['values'],
    value: FormFieldValue,
  ) => void;
  onSubmit: (formValues: Partial<VendorFormState['values']>) => Promise<void>;
  onReset?: (formValues: Partial<VendorFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
}

export default function VendorForm(props: VendorFormProps) {
  const {
    formState,
    onFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const navigate = useNavigate();

  const { data: contactData, loading: contactsLoading } = useQuery(
    GET_CONTACTS_QUERY);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();

      setIsSubmitting(true);
      try {
        await onSubmit(formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit],
  );

  const handleSelectFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof VendorFormState['values'],
        Number(event.target.value),
      );
    },
    [onFieldChange],
  );

  const handleStateFieldChange = useCallback(
    (stateAbbreviation: string) => {
      onFieldChange(
        'state',
        stateAbbreviation
      );
    },
    [onFieldChange],
  );

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof VendorFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = useCallback(() => {
    navigate(backButtonPath ?? '/dashboard/vendors');
  }, [navigate, backButtonPath]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 10, sm: 4 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.name ?? ''}
              onChange={handleTextFieldChange}
              name="name"
              label="Name"
              error={!!formErrors.name}
              helperText={formErrors.name ?? ' '}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.address ?? ''}
              onChange={handleTextFieldChange}
              name="address"
              label="Address"
              autoComplete="address-line1"
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.city ?? ''}
              onChange={handleTextFieldChange}
              name="city"
              label="City"
              autoComplete="address-level2"
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }} sx={{ display: 'flex' }}>
            <Autocomplete
              getOptionLabel={(option) => option.name}
              options={states || []}
              onChange={(_, newValue: State | null) => {
                handleStateFieldChange(
                  newValue?.abbreviation || ''
                );
              }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  autoComplete="address-level1"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                />
              )}
              renderOption={(props, state) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    {...optionProps}
                  >
                    {state.name}
                  </Box>
                );
              }}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.zip ?? ''}
              onChange={handleTextFieldChange}
              name="zip"
              label="Zip"
              autoComplete="postal-code"
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 8, sm: 4 }} sx={{ display: 'flex' }}>
            <TextField
              select
              value={String(formValues?.contactId ?? '0')}
              onChange={handleSelectFieldChange}
              label="Contact"
              name="contactId"
              slotProps={{ inputLabel: { shrink: true } }}
              defaultValue=""
              fullWidth
            >
              <MenuItem value="0" disabled>Select Contact</MenuItem>
              {contactsLoading ? (
                <MenuItem disabled>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading...</Typography>
                  </Box>
                </MenuItem>
              ) : (
                (contactData?.contacts || [])?.map((contact) => (
                  <MenuItem key={contact.id} value={contact.id}>{contact.name}</MenuItem>))
              )}
            </TextField>
          </Grid>
        </Grid>
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          color="success"
          startIcon={<SaveIcon />}
          loading={isSubmitting}
        >
          {submitButtonLabel}
        </Button>
      </Stack>
    </Box>
  );
}
